"use client";

import { Minus, Plus } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
const labelVariants = cva("font-medium capitalize text-muted-foreground", {
  variants: {
    size: {
      default: "w-full text-md",
      sm: "flex-1 text-sm"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
const controlsVariants = cva("flex items-center justify-between", {
  variants: {
    size: {
      default: "md:gap-2 w-full md:max-w-56",
      sm: "w-40 shrink-0"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
const stepButtonVariants = cva("rounded-full md:bg-muted hover:bg-muted-strong cursor-pointer flex items-center justify-center border border-border md:border-none", {
  variants: {
    size: {
      default: "h-9 w-9 [&_svg]:h-4 [&_svg]:w-4",
      sm: "h-7 w-7 [&_svg]:h-3.5 [&_svg]:w-3.5"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
const valueVariants = cva("capitalize text-nowrap text-foreground text-center rounded-lg", {
  variants: {
    size: {
      default: "text-sm md:text-md px-4 py-2",
      sm: "text-sm px-1 min-w-16"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
type IncrementorProps = VariantProps<typeof controlsVariants> & {
  value?: number | string;
  title?: string;
  allowNegative?: boolean;
  type?: "default" | "moreThan";
  labelOnZero?: string;
  onChange?: (value: number | string) => void;
  className?: string;
  labelClassName?: string;
  controlsClassName?: string;
};
export function Incrementor({
  value = 0,
  title,
  labelOnZero,
  allowNegative = false,
  type = "default",
  className,
  labelClassName,
  controlsClassName,
  size,
  onChange
}: IncrementorProps) {
  const increment = () => {
    if (onChange) {
      onChange((value as number) + 1);
    }
  };
  const decrement = () => {
    if (allowNegative || value as number > 0) {
      if (onChange) {
        onChange((value as number) - 1);
      }
    }
  };
  return <div className={cn("flex flex-col sm:flex-row  sm:items-center gap-4 justify-between", className)} data-sentry-component="Incrementor" data-sentry-source-file="Incrementor.tsx">
      {title && <div tabIndex={-1} className={cn(labelVariants({
      size
    }), labelClassName)}>
          {title}
        </div>}

      <div className={cn(controlsVariants({
      size
    }), controlsClassName)}>
        <div data-testid="incrementor-decrement" onClick={decrement} className={cn(stepButtonVariants({
        size
      }), !allowNegative && typeof value === "number" && value < 1 && "cursor-not-allowed bg-muted text-faint-foreground")}>
          <Minus data-sentry-element="Minus" data-sentry-source-file="Incrementor.tsx" />
        </div>

        <div className={valueVariants({
        size
      })} tabIndex={-1}>
          {labelOnZero && value === 0 ? labelOnZero : value}
          {type === "moreThan" && value !== 0 && ` +`}
        </div>

        <div data-testid="incrementor-increment" onClick={increment} className={stepButtonVariants({
        size
      })}>
          <Plus data-sentry-element="Plus" data-sentry-source-file="Incrementor.tsx" />
        </div>
      </div>
    </div>;
}